import { useState, useEffect } from "react";
import type { AnsweredQuestion, BBox } from "@/api/q-and-a";
import { getCitationBboxes, type CitationSignerHints } from "@/api/documents";
import { mergeOverlappingHighlights } from "../utils/citationProcessing";
import { ExtendedHighlight } from "@/api/highlights";
import type { HighlightMetaIndex } from "../types";

const EMPTY_META_INDEX: HighlightMetaIndex = {
  meta: new Map(),
  byCitation: new Map(),
};

/**
 * Build an area highlight from an array of bounding boxes.
 */
const bboxesToAreaHighlight = (
  bboxes: BBox[],
  citationId: string,
  page: number,
  questionText: string,
): ExtendedHighlight => {
  const minLeft = Math.min(...bboxes.map((b) => b.Left));
  const minTop = Math.min(...bboxes.map((b) => b.Top));
  const maxRight = Math.max(...bboxes.map((b) => b.Left + b.Width));
  const maxBottom = Math.max(...bboxes.map((b) => b.Top + b.Height));

  const boundingRect = {
    x1: minLeft,
    y1: minTop,
    x2: maxRight,
    y2: maxBottom,
    width: 1,
    height: 1,
    pageNumber: page,
  };

  return {
    id: `citation-${citationId}`,
    type: "area",
    position: {
      boundingRect,
      rects: [boundingRect],
      usePdfCoordinates: false,
    },
    comment: { text: questionText, emoji: "🔍" },
  };
};

/**
 * Hook for processing and managing citation highlights in PDF documents.
 *
 * This hook processes Q&A data to extract citations, searches for them in the PDF,
 * and creates visual highlights. It also handles merging overlapping highlights
 * and maintains citation metadata for display.
 *
 * @param documentAnswersData - Array of answered questions containing citations
 * @param pdfDocumentLoaded - Boolean indicating if the PDF document is fully loaded
 * @param searchText - Function to search for text in the PDF document
 *
 * @returns An object containing:
 * - citationHighlights: Array of ExtendedHighlight objects for rendering citations
 * - citationDataMap: Map containing citation metadata (question, answer, explanation)
 * - highlightMetaIndex: Index mapping highlight IDs and citation IDs for O(1) lookups
 */
export const useCitationHighlights = (
  documentAnswersData: AnsweredQuestion[] | undefined,
  pdfDocumentLoaded: boolean,
  searchText: (
    text: string,
    specificPage?: number | null,
  ) => Promise<ExtendedHighlight[]>,
  documentId?: string,
) => {
  const [citationHighlights, setCitationHighlights] = useState<
    Array<ExtendedHighlight>
  >([]);
  const [citationDataMap, setCitationDataMap] = useState<Map<string, any>>(
    new Map(),
  );
  const [highlightMetaIndex, setHighlightMetaIndex] = useState<HighlightMetaIndex>(EMPTY_META_INDEX);

  // Process Q&A data and extract citations with proper text search
  useEffect(() => {
    let cancelled = false;

    const processCitations = async () => {
      if (documentAnswersData && pdfDocumentLoaded) {
        // Extract citations from answered questions (updated for answer groups)
        const allCitations = documentAnswersData
          .flatMap((aq) =>
            aq.answer_groups.flatMap((group) =>
              group.answers.flatMap((answer) => {
                // Signature answers carry per-signer metadata. When the
                // signer card has many citations (one signer signing on
                // many pages), each citation needs the matching
                // signature_data row's identity for bbox disambiguation —
                // the page on the citation is the key. Service-block /
                // multi-signer pages share the same page number across
                // signers, so without per-citation hints the matcher
                // falls back to fuzzy text search and lands on the wrong
                // name. Index signature_data by (doc_id, page) so each
                // citation can pick out its own row.
                const sigByDocPage = new Map<string, NonNullable<typeof answer.signature_data>[number]>();
                for (const sig of answer.signature_data ?? []) {
                  const docId = sig.source_document_id ?? "";
                  const page = sig.page ?? 0;
                  sigByDocPage.set(`${docId}|${page}`, sig);
                }
                return answer.citations
                  .filter((citation) => !documentId || citation.document_id === documentId)
                  .map((citation) => {
                    const sigKey = `${citation.document_id}|${citation.page ?? 0}`;
                    const sig = sigByDocPage.get(sigKey)
                      // Single-signature answers (or single-page docs) —
                      // legacy fallback when the (doc_id, page) lookup
                      // misses but the answer has exactly one signer.
                      ?? (answer.signature_data?.length === 1 ? answer.signature_data[0] : undefined);
                    const signerHints: CitationSignerHints | undefined = sig
                      ? {
                          name: sig.signer_name || sig.raw_signer_name || undefined,
                          role: sig.signer_role || undefined,
                          company: sig.signer_company || undefined,
                        }
                      : undefined;
                    return {
                      citationId: citation.id,
                      answerId: answer.id,
                      questionId: aq.question_id,
                      answerGroupId: group.id,
                      citation: citation.citation,
                      page: citation.page,
                      bboxes: (!citation.extracted_from_project && citation.bboxes) || null,
                      questionText: aq.question_text,
                      answerText: answer.text,
                      answerExplanation: answer.explanation,
                      isCurrentValue: answer.is_current_value,
                      isComposite: answer.is_composite,
                      documentId: citation.document_id,
                      filename: citation.filename,
                      signerHints,
                    };
                  });
              })
            )
          );

        // Deduplicate citations by citationId to avoid creating multiple highlights for the same citation
        // When multiple answers reference the same citation, we should only create one highlight
        const citationMap = new Map<string, typeof allCitations[0]>();
        allCitations.forEach(citation => {
          const existingCitation = citationMap.get(citation.citationId);
          if (!existingCitation) {
            // First occurrence of this citation
            citationMap.set(citation.citationId, citation);
          }
        });

        const extractedCitations = Array.from(citationMap.values());

        const citationHighlights: ExtendedHighlight[] = [];
        const newCitationDataMap = new Map();
        const metaMap = new Map<string, any>();
        const byCitationMap = new Map<string, string>();

        const storeCitationData = (citation: (typeof extractedCitations)[0], highlightId: string) => {
          newCitationDataMap.set(highlightId, {
            questionId: citation.questionId,
            answerGroupId: citation.answerGroupId,
            questionText: citation.questionText,
            answerText: citation.answerText,
            answerExplanation: citation.answerExplanation,
            isCurrentValue: citation.isCurrentValue,
            isComposite: citation.isComposite,
            documentId: citation.documentId,
            filename: citation.filename,
          });
          metaMap.set(highlightId, {
            highlightId,
            citationIds: [citation.citationId],
            questionIds: [citation.questionId],
            answerGroupIds: [citation.answerGroupId],
            kind: "citation" as const,
          });
          byCitationMap.set(citation.citationId, highlightId);
        };

        // First pass: search PDF text layer (sequential — depends on PDF.js state).
        // Signature citations are LLM-generated prose descriptions ("DocuSign
        // signature above printed name 'X' and title 'Y'") rather than verbatim
        // PDF text, and fuse.js fuzzy matching inside searchText routinely
        // lands them on the wrong name/role line when many similar signer
        // blocks share the page. Skip the text layer entirely for those — the
        // backend has already computed signer-disambiguated bboxes.
        const bboxFallbacks: (typeof extractedCitations)[0][] = [];
        for (const citation of extractedCitations) {
          if (!citation.citation) continue; // ignore empty citations
          try {
            const hasSignerHints = !!citation.signerHints;
            const searchHighlights = hasSignerHints
              ? []
              : await searchText(citation.citation, citation.page);

            if (searchHighlights.length > 0) {
              const bestMatch = searchHighlights[0];
              const highlightId = `citation-${citation.citationId}`;
              citationHighlights.push({
                ...bestMatch,
                id: highlightId,
                type: "text" as const,
                comment: { text: citation.questionText, emoji: "🔍" },
              });
              storeCitationData(citation, highlightId);
            } else if (citation.page != null) {
              // Text layer search skipped or unmatched — try pre-computed bboxes first
              if (citation.bboxes && citation.bboxes.length > 0) {
                const areaHighlight = bboxesToAreaHighlight(
                  citation.bboxes,
                  citation.citationId,
                  citation.page,
                  citation.questionText,
                );
                citationHighlights.push(areaHighlight);
                storeCitationData(citation, areaHighlight.id);
              } else if (citation.documentId) {
                // No pre-computed bboxes — fall back to API
                bboxFallbacks.push(citation);
              }
            }
          } catch (error) {
            console.error(
              "Error processing citation:",
              citation.citation,
              error,
            );
          }
        }

        // Second pass: fetch Textract bboxes via API for citations without pre-computed data
        if (bboxFallbacks.length > 0) {
          const results = await Promise.allSettled(
            bboxFallbacks.map((citation) =>
              getCitationBboxes(
                citation.documentId!,
                citation.page!,
                citation.citation!,
                citation.signerHints,
              ).then((result) => ({ citation, ...result })),
            ),
          );

          for (const result of results) {
            if (result.status !== "fulfilled") continue;
            const { citation, bboxes, matched } = result.value;
            if (!matched || bboxes.length === 0) continue;

            const areaHighlight = bboxesToAreaHighlight(
              bboxes,
              citation.citationId,
              citation.page!,
              citation.questionText,
            );
            citationHighlights.push(areaHighlight);
            storeCitationData(citation, areaHighlight.id);
          }
        }

        const initialMetaIndex: HighlightMetaIndex = {
          meta: metaMap,
          byCitation: byCitationMap,
        };

        if (cancelled) return;

        // Merge overlapping highlights
        const { mergedHighlights, updatedCitationDataMap, updatedMetaIndex } =
          mergeOverlappingHighlights(citationHighlights, newCitationDataMap, initialMetaIndex);

        // Update the citation data map, highlights, and meta index
        setCitationDataMap(updatedCitationDataMap);
        setCitationHighlights(mergedHighlights);
        setHighlightMetaIndex(updatedMetaIndex);
      }
    };

    processCitations();
    return () => { cancelled = true; };
  }, [documentAnswersData, pdfDocumentLoaded, searchText, documentId]);

  return {
    citationHighlights,
    citationDataMap,
    highlightMetaIndex,
  };
};
